/**
 * PointsHistoryDialog - Dialog zur Anzeige der Punktehistorie eines Kunden
 */
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { toast } from 'sonner';
import { formatSwiss } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PointsHistoryDialog = ({
  open,
  onOpenChange,
  pointTransactions,
  customerDetails,
  onRefresh
}) => {
  const handleReverseTransaction = async (transactionId) => {
    if (!window.confirm('Diese Punktebuchung wirklich rückgängig machen?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/customers/${customerDetails.id}/point-transactions/${transactionId}/reverse`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Punktebuchung wurde rückgängig gemacht');
      onRefresh();
    } catch (error) {
      console.error('Error reversing transaction', error);
      const msg = error.response?.data?.detail || 'Fehler beim Rückgängig machen';
      toast.error(msg);
    }
  };

  const getTypeIcon = (type) => ({
    purchase: '💰',
    booking: '📅',
    refund: '↩️',
    no_show_penalty: '❌'
  }[type] || '📝');

  const getTypeLabel = (type) => ({
    purchase: 'Kauf',
    booking: 'Buchung',
    refund: 'Rückerstattung',
    no_show_penalty: 'No-Show'
  }[type] || type);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle>Punktehistorie</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {pointTransactions.length === 0 ? (
            <p className="text-center text-gray-500 py-8">Keine Transaktionen vorhanden</p>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {pointTransactions.map((transaction) => {
                const isPositive = transaction.points_amount > 0;

                return (
                  <div key={transaction.id} className="p-3 border rounded-lg hover:bg-gray-50">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{getTypeIcon(transaction.transaction_type)}</span>
                        <div>
                          <p className="font-medium">{getTypeLabel(transaction.transaction_type)}</p>
                          <p className="text-sm text-gray-600">{transaction.description}</p>
                          {transaction.subscription_id && (
                            <p className="text-xs text-blue-600 font-medium mt-1">
                              🎫 Abgebucht von Abo
                            </p>
                          )}
                          <p className="text-xs text-gray-400">
                            {formatSwiss(transaction.created_at, {
                              day: '2-digit', month: '2-digit', year: 'numeric',
                              hour: '2-digit', minute: '2-digit',
                            })}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-lg font-bold ${
                          isPositive ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {isPositive ? '+' : ''}{transaction.points_amount} Punkte
                        </p>
                        <p className="text-sm text-gray-500">
                          Saldo: {transaction.balance_after}
                        </p>
                      </div>
                      {!transaction.invoice_id && transaction.transaction_type === 'purchase' && !transaction.reversed && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleReverseTransaction(transaction.id)}
                        >
                          Rückgängig
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div className="flex justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Schliessen
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PointsHistoryDialog;
